import { fetchText, fetchArrayBuffer } from '../utils/http';
import { arrayBufferToBase64, escapeClosingScript } from '../utils/base64';

const ensureOutputWrapper = (html) => {
  if (!html) {
    return '<div id="wasm-output"></div>';
  }
  return html.includes('id="wasm-output"') ? html : `${html}<div id="wasm-output"></div>`;
};

const parseConfigFromDataset = (el) => {
  if (!el?.dataset?.config) {
    return {};
  }
  try {
    return JSON.parse(el.dataset.config);
  } catch (error) {
    console.error('Unable to parse wasm playground config', error);
    return {};
  }
};

export const registerWasmPlayground = (Alpine) => {
  Alpine.data('wasmPlayground', (overrides = {}) => ({
    status: 'Preparing experiment…',
    iframeSrc: '',
    assets: {
      html: '',
      helperScript: '',
      wasmExec: '',
      wasmBase64: '',
    },
    config: { ...overrides },
    async init() {
      if (!Object.keys(this.config).length) {
        this.config = parseConfigFromDataset(this.$el);
      }
      await this.refresh();
    },
    async refresh() {
      try {
        this.status = 'Loading assets…';
        await this.loadAssets();
        this.status = 'Experiment ready';
        this.iframeSrc = this.buildSrcDoc();
      } catch (error) {
        console.error('Unable to load WASM experiment', error);
        this.status = `Error: ${error.message}`;
      }
    },
    async loadAssets() {
      const { htmlFile, helperFile, wasmFile, wasmExec } = this.config;
      if (!wasmFile || !wasmExec) {
        throw new Error('wasmFile and wasmExec are required to run this experiment.');
      }
      const [htmlContent, helperContent, execContent, wasmBuffer] = await Promise.all([
        htmlFile ? fetchText(htmlFile) : Promise.resolve(''),
        helperFile ? fetchText(helperFile) : Promise.resolve(''),
        fetchText(wasmExec),
        fetchArrayBuffer(wasmFile),
      ]);
      this.assets = {
        html: htmlContent || '',
        helperScript: helperContent || '',
        wasmExec: execContent || '',
        wasmBase64: arrayBufferToBase64(wasmBuffer),
      };
    },
    rerun() {
      if (!this.assets.wasmExec || !this.assets.wasmBase64) {
        this.status = 'Waiting for assets…';
        return;
      }
      this.status = 'Re-running experiment…';
      this.iframeSrc = this.buildSrcDoc();
    },
    buildSrcDoc() {
      const wasmExecEscaped = escapeClosingScript(this.assets.wasmExec);
      const helperScriptEscaped = escapeClosingScript(this.assets.helperScript);
      const runnerScript = escapeClosingScript(`
        (async () => {
          try {
            const go = new Go();
            const output = document.getElementById('wasm-output');
            document.write = (str) => {
              if (output) {
                output.innerHTML += str;
              }
              console.log('document.write called with:', str);
            };
            const wasmBytes = new Uint8Array(atob('${this.assets.wasmBase64}').split('').map((c) => c.charCodeAt(0)));
            const result = await WebAssembly.instantiate(wasmBytes, go.importObject);
            go.run(result.instance);
            window.wasmInstance = result.instance;
            ${helperScriptEscaped}
          } catch (error) {
            console.error('Detailed error:', error);
            const output = document.getElementById('wasm-output');
            if (output) {
              output.innerHTML += '<pre style="color:red;">' + error.message + '</pre>';
            }
          }
        })();
      `);
      const html = ensureOutputWrapper(this.assets.html);
      return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <style>
    html, body { margin: 0; padding: 0; width: 100%; height: 100%; background: #fff; }
  </style>
</head>
<body>
  ${html}
  <script>${wasmExecEscaped}</script>
  <script>${runnerScript}</script>
</body>
</html>`;
    },
  }));
};
